/**
 * PreviewIssueFeed.jsx
 *
 * Admin panel that renders card-template preview issues read from the
 * public browser-storage feedback key (kea_storefront_preview_feedback_v1).
 *
 * Contract:
 *   - Reads only the public AdminPreviewIssue shape via usePreviewFeedback hook.
 *   - Shows fallback indicator when a template_compile_error / template_render_error
 *     / template_unsafe_markup error exists — indicating the storefront fell back
 *     to the default preset card.
 *   - Marks feed as stale when updated_at is older than 15 minutes.
 *   - Stays out of the page when there is no preview feedback to act on.
 *   - Updates automatically through usePreviewFeedback polling; no manual refresh is needed.
 *
 * Source: 07-preview-storage-and-feedback-contract.md §3, §4
 *         Epic 15 task 3 "Preview feedback and fallback visibility"
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Chip,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeStyles } from '@material-ui/core/styles';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    background: '#fafafa',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
  },
  staleNotice: {
    fontSize: 11,
    color: theme.palette.warning.dark,
    marginBottom: theme.spacing(1),
  },
  issueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  issueSeverityChip: {
    marginRight: theme.spacing(1),
    flexShrink: 0,
    fontSize: 11,
    height: 20,
  },
  issueCode: {
    fontFamily: '"Roboto Mono", monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginBottom: 2,
  },
  issueMessage: {
    fontSize: 13,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  contextRow: {
    marginTop: 2,
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  contextKey: {
    fontFamily: '"Roboto Mono", monospace',
  },
  fallbackBanner: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    background: '#fff3e0',
    borderLeft: '4px solid #ff9800',
    borderRadius: 2,
    marginBottom: theme.spacing(1.5),
  },
  noIssues: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.success.main,
    padding: theme.spacing(1),
  },
}));

// Context keys that are useful to display for template issues
const DISPLAYED_CONTEXT_KEYS = ['template_id', 'template_hash', 'view', 'search_box_id'];

function IssueContextDisplay({ context }) {
  const classes = useStyles();
  const entries = DISPLAYED_CONTEXT_KEYS
    .filter((k) => context[k] !== undefined && context[k] !== '')
    .map((k) => [k, context[k]]);

  if (entries.length === 0) return null;

  return (
    <Box className={classes.contextRow}>
      {entries.map(([key, value]) => (
        <span key={key} style={{ marginRight: 8 }}>
          <span className={classes.contextKey}>{key}</span>
          {': '}
          <span>{String(value)}</span>
        </span>
      ))}
    </Box>
  );
}

IssueContextDisplay.propTypes = {
  context: PropTypes.object.isRequired,
};

/**
 * PreviewIssueFeed
 *
 * @param {{
 *   issues: Array,
 *   errorCount: number,
 *   warningCount: number,
 *   hasFallback: boolean,
 *   isStale: boolean,
 *   previewEnabled: boolean,
 *   updatedAt: string | null,
 * }} props  — shape returned by usePreviewFeedback()
 */
const PreviewIssueFeed = ({
  issues,
  errorCount,
  warningCount,
  hasFallback,
  isStale,
  previewEnabled,
  updatedAt,
}) => {
  const classes = useStyles();
  const t = useLocalizedMessage();

  if (!previewEnabled && issues.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root} data-testid="preview-issue-feed">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography className={classes.headerTitle}><IntlMessages id="ui.search.customization.cardTemplate.previewIssueFeed.previewIssues" /></Typography>
          {errorCount > 0 && (
            <Chip
              label={t('search.cardTemplate.preview.errors', { count: errorCount })}
              size="small"
              color="secondary"
              style={{ marginLeft: 8, height: 20, fontSize: 11 }}
              data-testid="error-count-chip"
            />
          )}
          {warningCount > 0 && (
            <Chip
              label={t('search.cardTemplate.preview.warnings', { count: warningCount })}
              size="small"
              style={{ marginLeft: 8, height: 20, fontSize: 11, background: '#fff8e1' }}
              data-testid="warning-count-chip"
            />
          )}
        </Box>
      </Box>

      {/* ── Stale warning ───────────────────────────────────────────── */}
      {isStale && (
        <Typography className={classes.staleNotice} data-testid="stale-notice">
          <IntlMessages id="ui.search.customization.cardTemplate.previewIssueFeed.feedbackMayBeOutdatedLastUpdated" />{' '}{updatedAt || t('common.unknown')}<IntlMessages id="ui.search.customization.cardTemplate.previewIssueFeed.runAnotherStorefrontPreviewToUpdateIt" />
        </Typography>
      )}

      {/* ── Fallback indicator ──────────────────────────────────────── */}
      {hasFallback && (
        <Box className={classes.fallbackBanner} data-testid="fallback-banner">
          <WarningIcon style={{ color: '#ff9800', marginRight: 8, fontSize: 20 }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            <IntlMessages id="ui.search.customization.cardTemplate.previewIssueFeed.customTemplateIsInvalidStorefrontIsUsingTheFallback" />
          </Typography>
        </Box>
      )}

      {/* ── No issues ───────────────────────────────────────────────── */}
      {issues.length === 0 && (
        <Box className={classes.noIssues} data-testid="no-issues-message">
          <CheckCircleIcon style={{ marginRight: 8, fontSize: 18 }} />
          <Typography variant="body2"><IntlMessages id="ui.search.customization.cardTemplate.previewIssueFeed.noTemplateIssuesDetectedInPreview" /></Typography>
        </Box>
      )}

      {/* ── Issue list ──────────────────────────────────────────────── */}
      {issues.map((issue) => (
        <Box key={issue.id || issue.dedupe_key} className={classes.issueRow} data-testid="issue-row">
          <Box flex={1}>
            <Box display="flex" alignItems="center" mb={0.25}>
              <Chip
                label={issue.severity}
                size="small"
                className={classes.issueSeverityChip}
                style={{
                  background: issue.severity === 'error' ? '#ffebee' : '#fff8e1',
                  color: issue.severity === 'error' ? '#c62828' : '#e65100',
                }}
                icon={
                  issue.severity === 'error'
                    ? <ErrorIcon style={{ fontSize: 12 }} />
                    : <WarningIcon style={{ fontSize: 12 }} />
                }
                data-testid={`issue-severity-${issue.severity}`}
              />
              <Typography className={classes.issueCode} data-testid="issue-code">
                {issue.code}
              </Typography>
            </Box>
            <Typography className={classes.issueMessage} data-testid="issue-message">
              {issue.message}
            </Typography>
            {issue.context && Object.keys(issue.context).length > 0 && (
              <IssueContextDisplay context={issue.context} />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

PreviewIssueFeed.propTypes = {
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      dedupe_key: PropTypes.string,
      severity: PropTypes.oneOf(['error', 'warning']),
      code: PropTypes.string,
      message: PropTypes.string,
      context: PropTypes.object,
    }),
  ).isRequired,
  errorCount: PropTypes.number.isRequired,
  warningCount: PropTypes.number.isRequired,
  hasFallback: PropTypes.bool.isRequired,
  isStale: PropTypes.bool.isRequired,
  previewEnabled: PropTypes.bool.isRequired,
  updatedAt: PropTypes.string,
};

PreviewIssueFeed.defaultProps = {
  updatedAt: null,
};

export default PreviewIssueFeed;
